import { useState, type FormEvent } from 'react'
import { Head, router, usePage } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ArrowLeft, Save } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface User {
  id: number
  email: string
  isAdmin: boolean
}

interface Props extends Omit<AppShellProps, 'children'> {
  user: User
  isAdminEdit: boolean
  canSetAdmin: boolean
  urls: AppShellProps['urls'] & {
    updateAsAdmin: string
    updateSelf: string
    back: string
  }
  translations: AppShellProps['translations'] & {
    editUser: string
    email: string
    password: string
    passwordConfirmation: string
    currentPassword: string
    admin: string
    save: string
    back: string
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Edit(props: Props) {
  const { user, isAdminEdit, canSetAdmin, urls, translations: t, ...shell } = props
  const { flash } = usePage<{ flash: { notice?: string; alert?: string } }>().props

  const [form, setForm] = useState({
    email: user.email,
    current_password: '',
    password: '',
    password_confirmation: '',
    is_admin: user.isAdmin,
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    const payload = isAdminEdit
      ? {
          user: {
            email: form.email,
            password: form.password,
            password_confirmation: form.password_confirmation,
            is_admin: form.is_admin,
          },
          id: user.id,
        }
      : {
          user: {
            email: form.email,
            current_password: form.current_password,
            password: form.password,
            password_confirmation: form.password_confirmation,
          },
        }

    router.put(isAdminEdit ? urls.updateAsAdmin : urls.updateSelf, payload, {
      preserveScroll: true,
    })
  }

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title={t.editUser} />

      <div className="mx-auto max-w-2xl space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => router.visit(urls.back)}
            className="text-[#91918E] hover:bg-[#F1F1EF]"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-semibold text-[#37352F]">{t.editUser}</h1>
        </div>

        {/* Flash messages */}
        {flash?.notice && (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            {flash.notice}
          </div>
        )}
        {flash?.alert && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {flash.alert}
          </div>
        )}

        {/* Form */}
        <Card className="border-[#E8E8E5]">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-[#37352F]">{t.editUser}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#37352F]">
                  {t.email}
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="off"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="border-[#E8E8E5] focus-visible:ring-[#2383E2]"
                />
              </div>

              {!isAdminEdit && (
                <div className="space-y-2">
                  <Label htmlFor="current_password" className="text-[#37352F]">
                    {t.currentPassword}
                  </Label>
                  <Input
                    id="current_password"
                    type="password"
                    autoComplete="current-password"
                    value={form.current_password}
                    onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                    className="border-[#E8E8E5] focus-visible:ring-[#2383E2]"
                    placeholder="Required only when changing password"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#37352F]">
                  {t.password}
                </Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="border-[#E8E8E5] focus-visible:ring-[#2383E2]"
                  placeholder="Leave blank to keep current"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password_confirmation" className="text-[#37352F]">
                  {t.passwordConfirmation}
                </Label>
                <Input
                  id="password_confirmation"
                  type="password"
                  value={form.password_confirmation}
                  onChange={(e) => setForm({ ...form, password_confirmation: e.target.value })}
                  className="border-[#E8E8E5] focus-visible:ring-[#2383E2]"
                  placeholder="Leave blank to keep current"
                />
              </div>

              {canSetAdmin && (
                <div className="flex items-center gap-3">
                  <input
                    id="is_admin"
                    type="checkbox"
                    checked={form.is_admin}
                    onChange={(e) => setForm({ ...form, is_admin: e.target.checked })}
                    className="h-4 w-4 rounded border-[#E8E8E5] text-[#2383E2] focus:ring-[#2383E2]"
                  />
                  <Label htmlFor="is_admin" className="text-[#37352F]">
                    {t.admin}
                  </Label>
                </div>
              )}

              <div className="flex items-center gap-3 pt-2">
                <Button
                  type="submit"
                  className="bg-[#2383E2] text-white hover:bg-[#1b6ec2]"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {t.save}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => router.visit(urls.back)}
                  className="border-[#E8E8E5] text-[#37352F] hover:bg-[#F1F1EF]"
                >
                  {t.back}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  )
}
